// Egress render template (spec v2 §5). LiveKit egress (headless Chrome) loads
// this, injects the room url+token, and records what it renders. We show ONLY the
// program composite + audio — no chat, no controls — reusing BroadcastView so the
// recording/simulcast match the subscriber view. Not a user-facing surface.
//
// The recorder waits for the egress-SDK `setRoom` signal before capturing; without
// it the egress aborts with no output. EgressBridge sends that signal once the
// room connects. url+token come from EgressHelper (the recorder injects them),
// falling back to ?url=&token= for manual testing.

import { LiveKitRoom, RoomAudioRenderer, useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';
import EgressHelper from '@livekit/egress-sdk';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ConnectionState, RoomEvent } from 'livekit-client';
import { useEffect } from 'react';

import { BroadcastView } from '@/features/live/BroadcastView';
import { roomQueries } from '@/features/live/roomQueries';
import { FeaturedSuperchatProvider } from '@/features/live/useFeaturedSuperchat';

interface EgressSearch {
  token: string;
  url: string;
}

export const Route = createFileRoute('/egress/$slug')({
  validateSearch: (search): EgressSearch => ({
    token: typeof search.token === 'string' ? search.token : '',
    url: typeof search.url === 'string' ? search.url : '',
  }),
  component: EgressRenderRoute,
});

// Drives the recorder: setRoom() registers the room (+ auto-end on disconnect),
// then startRecording() emits the START_RECORDING marker the recorder waits for.
// Without startRecording the egress sits in "Starting" until it times out; without
// either it "Aborted". Fired once the room is connected.
function EgressBridge() {
  const room = useRoomContext();
  useEffect(() => {
    if (!room) return;
    const begin = () => {
      EgressHelper.setRoom(room);
      EgressHelper.startRecording();
    };
    if (room.state === ConnectionState.Connected) begin();
    room.on(RoomEvent.Connected, begin);
    return () => {
      room.off(RoomEvent.Connected, begin);
    };
  }, [room]);
  return null;
}

function EgressRenderRoute() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  // Room metadata drives the overlays; rooms are world-readable, so this resolves
  // without a user session.
  const room = useQuery(roomQueries.bySlug(slug)).data;

  let url = search.url;
  let token = search.token;
  try {
    url = EgressHelper.getLiveKitURL() || url;
    token = EgressHelper.getAccessToken() || token;
  } catch {
    // Not loaded inside an egress recorder — fall back to the search params.
  }

  if (!token || !url || !room) return null;

  return (
    <LiveKitRoom
      token={token}
      serverUrl={url}
      connect
      video={false}
      audio={false}
      // Full resolution for the composite — not viewport-adaptive.
      options={{ adaptiveStream: false }}
      className="h-screen w-screen bg-black"
    >
      <EgressBridge />
      <FeaturedSuperchatProvider>
        <BroadcastView
          roomStatus={room.status}
          hostId={room.hostId}
          title={room.title}
          tickerLines={room.tickerLines}
          logoUrl={room.logoUrl}
        />
      </FeaturedSuperchatProvider>
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}
